import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AccountLimitsSection = ({ limits, usage }) => {
  const [showLimitIncrease, setShowLimitIncrease] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const calculatePercentage = (used, limit) => {
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-primary';
  };

  const limitData = [
    {
      id: 'daily',
      title: 'Daily Transfer Limit',
      limit: limits?.daily || 5000,
      used: usage?.daily || 1250,
      icon: 'Calendar',
      description: 'Resets every 24 hours'
    },
    {
      id: 'monthly',
      title: 'Monthly Transfer Limit',
      limit: limits?.monthly || 50000,
      used: usage?.monthly || 8750,
      icon: 'CalendarDays',
      description: 'Resets on the 1st of each month'
    },
    {
      id: 'annual',
      title: 'Annual Transfer Limit',
      limit: limits?.annual || 500000,
      used: usage?.annual || 45000,
      icon: 'CalendarRange',
      description: 'Resets on January 1st'
    }
  ];

  const handleRequestIncrease = () => {
    setShowLimitIncrease(true);
    // Simulate request submission
    setTimeout(() => {
      setShowLimitIncrease(false);
    }, 2000);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Account Limits</h2>
            <p className="text-sm text-muted-foreground">Monitor your transfer limits and usage</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleRequestIncrease}
          loading={showLimitIncrease}
          iconName="TrendingUp"
          iconPosition="left"
          iconSize={16}
        >
          Request Increase
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {limitData?.map((item) => {
          const percentage = calculatePercentage(item?.used, item?.limit);
          const remaining = item?.limit - item?.used;
          
          return (
            <div key={item?.id} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon name={item?.icon} size={16} className="text-primary" />
                  <h3 className="font-medium text-foreground">{item?.title}</h3>
                </div>
                <span className="text-xs text-muted-foreground">{percentage?.toFixed(0)}% used</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Used</span>
                  <span className="font-medium text-foreground">{formatCurrency(item?.used)}</span>
                </div>
                
                <div className="w-full bg-border rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(percentage)}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Remaining</span>
                  <span className="font-medium text-success">{formatCurrency(remaining)}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Limit</span>
                  <span className="font-semibold text-foreground">{formatCurrency(item?.limit)}</span>
                </div>

                <p className="text-xs text-muted-foreground mt-2">{item?.description}</p>
              </div>
            </div>
          );
        })}
      </div>
      {/* Limit Increase Information */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-foreground mb-1">Need Higher Limits?</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Complete your KYC verification and provide additional documentation to qualify for increased transfer limits. 
              Higher limits are available for verified business accounts and high-volume users.
            </p>
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="CheckCircle" size={12} className="text-success" />
                <span>Complete KYC verification</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="FileText" size={12} className="text-primary" />
                <span>Provide income documentation</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} className="text-warning" />
                <span>2-3 business days review</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountLimitsSection;